import type { AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";
import { generateToken } from "./jwt-service";
import type {
  ChatChannel,
  ChannelMessagesPayload,
  ChannelMessagesResponse,
  MemberTokenParams,
  MemberAccessTokenResponse,
  ChatLiabilityData,
} from "@/model/csr-chat";
import {
  CSR_SOCKET_USER_ROLE,
  CSR_SOCKET_USER_TYPE,
} from "./csr-chat-constants";

const CSR_CHAT_API = process.env.CSR_CHAT ?? "";

let chatToken = "";

export const getChatToken = (): string => chatToken;

const setChatToken = (token: string): void => {
  chatToken = token;
};

export type MemberInfo = {
  username: string;
  groupId: string;
  divisionId: string;
  memberId: string;
};

const memberHeaders = (info: MemberInfo) => ({
  Authorization: `Bearer ${getChatToken()}`,
  "Content-Type": "application/json",
  "Accept-Version": "1.0",
  username: info.username,
  group_id: info.groupId,
  division_id: info.divisionId,
  member_id: info.memberId,
  user_role: CSR_SOCKET_USER_ROLE,
  user_type: CSR_SOCKET_USER_TYPE,
});

export const getMembersChatToken = async (
  info: MemberInfo,
): Promise<MemberAccessTokenResponse> => {
  const memberJwtToken = await generateToken({
    sub: info.username,
    member_id: info.memberId,
  });

  const params: MemberTokenParams = {
    group_id: info.groupId,
    username: info.username,
  };

  const config: AxiosRequestConfig = {
    method: "POST",
    url: `${CSR_CHAT_API}/users/getMemberAccessToken`,
    headers: {
      Authorization: `Bearer ${memberJwtToken}`,
      group_id: info.groupId,
    },
    data: params,
  };

  const response: AxiosResponse<MemberAccessTokenResponse> =
    await axios(config);
  setChatToken(response.data?.accessCode ?? "");
  return response.data;
};

type ApiListResponse<T> = {
  data: T[];
};

export const getMyChatChannels = async (
  info: MemberInfo,
): Promise<ChatChannel[]> => {
  if (!getChatToken()) {
    throw new Error("[CHAT]: No chat token available.");
  }

  const config: AxiosRequestConfig = {
    method: "GET",
    url: `${CSR_CHAT_API}/users/chatChannels`,
    headers: memberHeaders(info),
  };

  const response: AxiosResponse<ApiListResponse<ChatChannel>> =
    await axios(config);
  if (response?.data?.statusCode !== 200)
    throw new Error("[CHAT]: Chat Channel API failed");
  return response.data?.data ?? [];
};

export const getChannelMessages = async (
  payload: ChannelMessagesPayload,
  info: MemberInfo,
): Promise<ChannelMessagesResponse> => {
  if (!getChatToken()) {
    throw new Error("[CHAT]: No chat token available.");
  }

  const config: AxiosRequestConfig = {
    method: "POST",
    url: `${CSR_CHAT_API}/users/getChannelMessages`,
    headers: memberHeaders(info),
    data: payload,
  };

  const response: AxiosResponse<ChannelMessagesResponse> = await axios(config);
  return {
    data: response.data?.data ?? [],
    newCount: response.data?.newCount ?? 0,
    oldCount: response.data?.oldCount ?? 0,
  };
};

export const getChatLiability = async (
  info: MemberInfo,
): Promise<ChatLiabilityData | null> => {
  if (!getChatToken()) {
    throw new Error("[CHAT]: No chat token available.");
  }

  const config: AxiosRequestConfig = {
    method: "GET",
    url: `${CSR_CHAT_API}/users/getClientStaticData`,
    headers: memberHeaders(info),
  };

  const response: AxiosResponse<ApiListResponse<ChatLiabilityData>> =
    await axios(config);
  if (response?.data?.statusCode !== 200)
    throw new Error("[CHAT]: Chat Info API failed");
  return response.data?.data ?? null;
};
